import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Icon, FormSection, Field, Checkbox, DAY_SHORT, DAY_NAMES } from './PrimaryUI';
import { STATIC_PRIMARY_ROUTES } from './primaryStaticData';

export const AMENITIES_LIST = ['WiFi', 'Charging Point', 'Blankets', 'Water Bottle', 'Reading Light', 'CCTV', 'TV', 'Snacks', 'GPS Tracking'];

// ------------------------------------------------------------
// Route selector (static)
// ------------------------------------------------------------
export const PrimaryRouteSelector = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const list = STATIC_PRIMARY_ROUTES.filter(r =>
    [r.routeName, r.localRouteName, r.routeNumber].some(v => (v || '').toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="pbo-route-select">
      <div className="prm-search pbo-route-input">
        <Icon name="map" size={16} />
        <input
          value={value ? `${value.localRouteName ? value.localRouteName + ' — ' : ''}${value.routeName}` : q}
          onClick={() => setOpen(!open)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onChange(null); }}
          placeholder="Select or search a primary route…" />
        {value && <button type="button" className="prm-search-clear" onClick={() => { onChange(null); setQ(''); }}>×</button>}
      </div>
      {open && (
        <div className="pbo-route-pop">
          {list.length === 0 ? <div className="pbo-route-empty">No primary routes found</div> : list.map(r => (
            <button key={r._id} type="button" className="pbo-route-opt" onClick={() => { onChange(r); setOpen(false); setQ(''); }}>
              <div className="top">
                {r.localRouteName && <span className="prm-tag navy">{r.localRouteName}</span>}
                <b>{r.routeNumber} · {r.routeName}</b>
                {r.creatorType === 'BusOwner' && <span className="prm-tag amber">Owner</span>}
              </div>
              <div className="sub">{r.origin.city} → {r.destination.city} · {r.routeDetails.totalDistance} km</div>
              <div className="prm-mini-days">{r.departureDays.map(d => <span key={d}>{DAY_SHORT[d]}</span>)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const sameDays = (a = [], b = []) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

// ------------------------------------------------------------
// ONE form for both "Add primary bus" and "Edit primary bus"
//   - add : <PrimaryBusFormModal owner={o} onSave={bus => ...} onClose={...} />
//   - edit: <PrimaryBusFormModal owner={o} bus={bus} onSave={bus => ...} onClose={...} />
// ------------------------------------------------------------
const PrimaryBusFormModal = ({ owner, bus, onClose, onSave }) => {
  const editing = !!bus;
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [route, setRoute] = useState(() => !bus ? null :
    STATIC_PRIMARY_ROUTES.find(r => r._id === bus.routeId || r.routeName === bus.routeName)
    || STATIC_PRIMARY_ROUTES.find(r => sameDays(r.departureDays, bus.departureDays)) || null);
  const [form, setForm] = useState({
    busNumber: bus?.busNumber || '', busName: bus?.busName || '',
    busType: bus?.busType || 'Non-AC', busCategory: bus?.busCategory || 'Seater',
    totalSeats: bus?.totalSeats ?? 40, amenities: bus?.amenities || [], isActive: bus ? !!bus.isActive : true
  });
  const [ticket, setTicket] = useState({
    instantTicket: bus ? !!bus.ticketBookingType?.instantTicket : true,
    seatBooking: bus ? !!bus.ticketBookingType?.seatBooking : false
  });
  const [pricing, setPricing] = useState(bus?.pricing || { basePrice: 100, baseDistance: 15, costPerKm: 4 });
  const steps = ['Basic info', 'Route & pricing', 'Amenities'];
  const keepsOwnDays = editing && bus.departureDays?.length > 0;

  const validate = (upTo = 2) => {
    if (upTo >= 0 && (!form.busNumber.trim() || !form.busName.trim())) { toast.error('Bus number and name are required'); return 0; }
    if (upTo >= 0 && !ticket.instantTicket && !ticket.seatBooking) { toast.error('Choose at least one ticket type'); return 0; }
    if (upTo >= 1 && !route && !keepsOwnDays) { toast.error('Please select a primary route'); return 1; }
    return -1;
  };

  const next = () => {
    const bad = validate(step);
    if (bad !== -1) return;
    setStep(step + 1);
  };

  const submit = () => {
    const bad = validate(2);
    if (bad !== -1) { setStep(bad); return; }
    setBusy(true);
    setTimeout(() => {
      onSave({
        ...(bus || {}),
        _id: bus?._id || 'b_' + Date.now(),
        busName: form.busName.trim(), busNumber: form.busNumber.trim().toUpperCase(),
        busType: form.busType, busCategory: form.busCategory, totalSeats: Number(form.totalSeats) || 0,
        isActive: form.isActive, isPrimaryBus: true,
        departureDays: route ? route.departureDays : bus.departureDays,
        routeId: route?._id || bus?.routeId, routeName: route?.routeName || bus?.routeName,
        ticketBookingType: { ...ticket }, amenities: form.amenities, pricing
      });
      toast.success(editing ? 'Bus updated' : 'Primary bus added');
      setBusy(false);
      onClose();
    }, 300);
  };

  const progress = Math.round(((step + 1) / steps.length) * 100);
  const toggleAmenity = (a) => setForm(p => ({ ...p, amenities: p.amenities.includes(a) ? p.amenities.filter(x => x !== a) : [...p.amenities, a] }));

  return (
    <div className="prm-overlay prm-overlay-top" onClick={onClose}>
      <div className="prm-form-modal prm-form-modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="prm-form-head">
          <div className="prm-form-head-row">
            <span className="prm-form-badge"><Icon name={editing ? 'edit' : 'bus'} size={24} /></span>
            <div>
              <h3>{editing ? 'Edit primary bus' : 'Add primary bus'}</h3>
              <p>{editing ? `${bus.busName} · ${bus.busNumber}` : `For ${owner.name} · ${owner.company}`}</p>
            </div>
          </div>
          <button className="prm-modal-close" onClick={onClose}>×</button>
          <div className="prm-progress">
            <div className="prm-progress-bar"><i style={{ width: `${progress}%` }} /></div>
            <span>Step {step + 1} of {steps.length} · {steps[step]}</span>
          </div>
        </div>

        <div className="pbo-stepnav">
          {steps.map((s, i) => (
            <button key={s} type="button" className={`pbo-stepnav-btn ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              onClick={() => { if (i <= step || editing) setStep(i); }}>
              <span>{i < step ? <Icon name="tick" size={12} /> : i + 1}</span>{s}
            </button>
          ))}
        </div>

        <div className="prm-form-body">
          {step === 0 && (
            <>
              <FormSection n="1" title="Bus information" hint="How the bus is identified on tickets.">
                <div className="prm-fgrid">
                  <Field label="Bus number" required><input className="prm-input" placeholder="e.g. WB01AB1234" value={form.busNumber} onChange={(e) => setForm(p => ({ ...p, busNumber: e.target.value }))} /></Field>
                  <Field label="Bus name" required><input className="prm-input" placeholder="e.g. Volvo Multi-Axle" value={form.busName} onChange={(e) => setForm(p => ({ ...p, busName: e.target.value }))} /></Field>
                  <Field label="Bus type">
                    <select className="prm-input prm-select" value={form.busType} onChange={(e) => setForm(p => ({ ...p, busType: e.target.value }))}>
                      {['AC', 'Non-AC', 'Volvo', 'Sleeper'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Category">
                    <select className="prm-input prm-select" value={form.busCategory} onChange={(e) => setForm(p => ({ ...p, busCategory: e.target.value }))}>
                      {['Seater', 'Sleeper', 'Seater+Sleeper'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Total seats"><input type="number" min="10" max="80" className="prm-input" value={form.totalSeats} onChange={(e) => setForm(p => ({ ...p, totalSeats: e.target.value }))} /></Field>
                </div>
                {editing && (
                  <div className="prm-checks" style={{ marginTop: 16 }}>
                    <Checkbox label="Bus is active" checked={form.isActive} onChange={() => setForm(p => ({ ...p, isActive: !p.isActive }))} />
                  </div>
                )}
              </FormSection>
              <FormSection n="2" title="Ticket booking" hint="Pick one or both.">
                <div className="prm-checks">
                  <Checkbox label="Instant ticket" checked={ticket.instantTicket} onChange={() => setTicket(p => ({ ...p, instantTicket: !p.instantTicket }))} />
                  <Checkbox label="Seat booking" checked={ticket.seatBooking} onChange={() => setTicket(p => ({ ...p, seatBooking: !p.seatBooking }))} />
                </div>
              </FormSection>
            </>
          )}

          {step === 1 && (
            <>
              <FormSection n="3" title="Primary route" hint="The bus follows this weekly schedule.">
                <PrimaryRouteSelector value={route} onChange={setRoute} />
                {route ? (
                  <div className="prm-callout teal" style={{ marginTop: 14 }}>
                    <Icon name="calendar" size={16} />
                    <div>
                      <strong>Weekly schedule</strong>
                      <span>
                        {route.departureDays.map(d => DAY_NAMES[d]).join(', ')} · departs {route.origin.departureTime} ·
                        arrives day {route.destination.arrivalDay} at {route.destination.arrivalTime}
                      </span>
                    </div>
                  </div>
                ) : keepsOwnDays && (
                  <div className="prm-callout amber" style={{ marginTop: 14 }}>
                    <Icon name="info" size={16} />
                    <div>
                      <strong>Current schedule kept</strong>
                      <span>Runs on {bus.departureDays.map(d => DAY_NAMES[d]).join(', ')}. Pick a route only if you want to change it.</span>
                    </div>
                  </div>
                )}
              </FormSection>
              <FormSection n="4" title="Pricing" hint="Static values for now.">
                <div className="prm-fgrid three">
                  <Field label="Base price (₹)"><input type="number" className="prm-input" value={pricing.basePrice} onChange={(e) => setPricing({ ...pricing, basePrice: e.target.value })} /></Field>
                  <Field label="Base distance (km)"><input type="number" className="prm-input" value={pricing.baseDistance} onChange={(e) => setPricing({ ...pricing, baseDistance: e.target.value })} /></Field>
                  <Field label="Cost per km (₹)"><input type="number" className="prm-input" value={pricing.costPerKm} onChange={(e) => setPricing({ ...pricing, costPerKm: e.target.value })} /></Field>
                </div>
              </FormSection>
            </>
          )}

          {step === 2 && (
            <FormSection n="5" title="Amenities" hint="Select everything available on board.">
              <div className="pbo-amen-grid">
                {AMENITIES_LIST.map(a => <Checkbox key={a} label={a} checked={form.amenities.includes(a)} onChange={() => toggleAmenity(a)} />)}
              </div>
            </FormSection>
          )}
        </div>

        <div className="prm-form-foot">
          <span className="prm-foot-hint">Fields marked <em>*</em> are required</span>
          <div className="prm-form-foot-actions">
            {step > 0 ? <button className="prm-btn ghost" onClick={() => setStep(step - 1)}>Back</button>
              : <button className="prm-btn ghost" onClick={onClose}>Cancel</button>}
            {editing && step < steps.length - 1 && <button className="prm-btn ghost" onClick={submit} disabled={busy}>Save now</button>}
            {step < steps.length - 1
              ? <button className="prm-btn primary" onClick={next}>Continue</button>
              : <button className="prm-btn primary" onClick={submit} disabled={busy}>
                  {busy ? 'Saving…' : <><Icon name="check" size={16} /> {editing ? 'Save changes' : 'Add primary bus'}</>}
                </button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimaryBusFormModal;